import {
    AppwriteException,
    Client,
    type Payload,
    applyDefaults,
} from '../client';
import type { Models } from '../models';

import { ScheduleResourceType } from '../enums/schedule-resource-type';
import { Status } from '../enums/status';
export class Projects {
    client: Client;

    constructor(client: Client) {
        this.client = client;
    }

    /**
     * List all billing addons for a project.
     *
     *
     * @param {string} params.projectId - Project ID
     * @throws {AppwriteException}
     * @returns {Promise<Models.AddonList>}
     */
    listAddons(params: { projectId: string }): Promise<Models.AddonList>;
    /**
     * List all billing addons for a project.
     *
     *
     * @param {string} projectId - Project ID
     * @throws {AppwriteException}
     * @returns {Promise<Models.AddonList>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    listAddons(projectId: string): Promise<Models.AddonList>;
    listAddons(
        paramsOrFirst: { projectId: string } | string,
    ): Promise<Models.AddonList> {
        let params: { projectId: string };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as { projectId: string };
        } else {
            params = {
                projectId: paramsOrFirst as string,
            };
        }

        const projectId = params.projectId;

        if (typeof projectId === 'undefined' || projectId === '') {
            throw new AppwriteException(
                'Missing required parameter: "projectId"',
            );
        }
        const apiPath = '/projects/{projectId}/addons'.replace(
            '{projectId}',
            encodeURIComponent(String(projectId)),
        );
        const apiPayload: Payload = {};
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            accept: 'application/json',
        };

        const request = this.client.call('get', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'addonList',
            models: {
                addonList: {
                    defaults: {
                        total: 0,
                        addons: [],
                    },
                    properties: {
                        addons: {
                            items: 'addon',
                        },
                    },
                },
                addon: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        $permissions: [],
                        key: '',
                        resourceType: '',
                        resourceId: '',
                        status: 'active',
                        currentValue: 0,
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Create a Premium Geo DB addon for a project.
     *
     *
     * @param {string} params.projectId - Project ID
     * @throws {AppwriteException}
     * @returns {Promise<Models.Addon>}
     */
    createPremiumGeoDBAddon(params: {
        projectId: string;
    }): Promise<Models.Addon>;
    /**
     * Create a Premium Geo DB addon for a project.
     *
     *
     * @param {string} projectId - Project ID
     * @throws {AppwriteException}
     * @returns {Promise<Models.Addon>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    createPremiumGeoDBAddon(projectId: string): Promise<Models.Addon>;
    createPremiumGeoDBAddon(
        paramsOrFirst: { projectId: string } | string,
    ): Promise<Models.Addon> {
        let params: { projectId: string };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as { projectId: string };
        } else {
            params = {
                projectId: paramsOrFirst as string,
            };
        }

        const projectId = params.projectId;

        if (typeof projectId === 'undefined' || projectId === '') {
            throw new AppwriteException(
                'Missing required parameter: "projectId"',
            );
        }
        const apiPath = '/projects/{projectId}/addons/premium-geo-db'.replace(
            '{projectId}',
            encodeURIComponent(String(projectId)),
        );
        const apiPayload: Payload = {};
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('post', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'addon',
            models: {
                addon: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        $permissions: [],
                        key: '',
                        resourceType: '',
                        resourceId: '',
                        status: 'active',
                        currentValue: 0,
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Get the details of a billing addon for a project.
     *
     *
     * @param {string} params.projectId - Project ID
     * @param {string} params.addonId - Addon ID
     * @throws {AppwriteException}
     * @returns {Promise<Models.Addon>}
     */
    getAddon(params: {
        projectId: string;
        addonId: string;
    }): Promise<Models.Addon>;
    /**
     * Get the details of a billing addon for a project.
     *
     *
     * @param {string} projectId - Project ID
     * @param {string} addonId - Addon ID
     * @throws {AppwriteException}
     * @returns {Promise<Models.Addon>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    getAddon(projectId: string, addonId: string): Promise<Models.Addon>;
    getAddon(
        paramsOrFirst: { projectId: string; addonId: string } | string,
        ...rest: [string?]
    ): Promise<Models.Addon> {
        let params: { projectId: string; addonId: string };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                projectId: string;
                addonId: string;
            };
        } else {
            params = {
                projectId: paramsOrFirst as string,
                addonId: rest[0] as string,
            };
        }

        const projectId = params.projectId;
        const addonId = params.addonId;

        if (typeof projectId === 'undefined' || projectId === '') {
            throw new AppwriteException(
                'Missing required parameter: "projectId"',
            );
        }
        if (typeof addonId === 'undefined' || addonId === '') {
            throw new AppwriteException(
                'Missing required parameter: "addonId"',
            );
        }
        const apiPath = '/projects/{projectId}/addons/{addonId}'
            .replace('{projectId}', encodeURIComponent(String(projectId)))
            .replace('{addonId}', encodeURIComponent(String(addonId)));
        const apiPayload: Payload = {};
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            accept: 'application/json',
        };

        const request = this.client.call('get', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'addon',
            models: {
                addon: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        $permissions: [],
                        key: '',
                        resourceType: '',
                        resourceId: '',
                        status: 'active',
                        currentValue: 0,
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Delete a billing addon for a project.
     *
     *
     * @param {string} params.projectId - Project ID
     * @param {string} params.addonId - Addon ID
     * @throws {AppwriteException}
     * @returns {Promise<{}>}
     */
    deleteAddon(params: { projectId: string; addonId: string }): Promise<{}>;
    /**
     * Delete a billing addon for a project.
     *
     *
     * @param {string} projectId - Project ID
     * @param {string} addonId - Addon ID
     * @throws {AppwriteException}
     * @returns {Promise<{}>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    deleteAddon(projectId: string, addonId: string): Promise<{}>;
    deleteAddon(
        paramsOrFirst: { projectId: string; addonId: string } | string,
        ...rest: [string?]
    ): Promise<{}> {
        let params: { projectId: string; addonId: string };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                projectId: string;
                addonId: string;
            };
        } else {
            params = {
                projectId: paramsOrFirst as string,
                addonId: rest[0] as string,
            };
        }

        const projectId = params.projectId;
        const addonId = params.addonId;

        if (typeof projectId === 'undefined' || projectId === '') {
            throw new AppwriteException(
                'Missing required parameter: "projectId"',
            );
        }
        if (typeof addonId === 'undefined' || addonId === '') {
            throw new AppwriteException(
                'Missing required parameter: "addonId"',
            );
        }
        const apiPath = '/projects/{projectId}/addons/{addonId}'
            .replace('{projectId}', encodeURIComponent(String(projectId)))
            .replace('{addonId}', encodeURIComponent(String(addonId)));
        const apiPayload: Payload = {};
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        return this.client.call('delete', uri, apiHeaders, apiPayload);
    }

    /**
     * Confirm payment for a billing addon for a project.
     *
     *
     * @param {string} params.projectId - Project ID
     * @param {string} params.addonId - Addon ID
     * @throws {AppwriteException}
     * @returns {Promise<Models.Addon>}
     */
    confirmAddonPayment(params: {
        projectId: string;
        addonId: string;
    }): Promise<Models.Addon>;
    /**
     * Confirm payment for a billing addon for a project.
     *
     *
     * @param {string} projectId - Project ID
     * @param {string} addonId - Addon ID
     * @throws {AppwriteException}
     * @returns {Promise<Models.Addon>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    confirmAddonPayment(
        projectId: string,
        addonId: string,
    ): Promise<Models.Addon>;
    confirmAddonPayment(
        paramsOrFirst: { projectId: string; addonId: string } | string,
        ...rest: [string?]
    ): Promise<Models.Addon> {
        let params: { projectId: string; addonId: string };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                projectId: string;
                addonId: string;
            };
        } else {
            params = {
                projectId: paramsOrFirst as string,
                addonId: rest[0] as string,
            };
        }

        const projectId = params.projectId;
        const addonId = params.addonId;

        if (typeof projectId === 'undefined' || projectId === '') {
            throw new AppwriteException(
                'Missing required parameter: "projectId"',
            );
        }
        if (typeof addonId === 'undefined' || addonId === '') {
            throw new AppwriteException(
                'Missing required parameter: "addonId"',
            );
        }
        const apiPath = '/projects/{projectId}/addons/{addonId}/confirmations'
            .replace('{projectId}', encodeURIComponent(String(projectId)))
            .replace('{addonId}', encodeURIComponent(String(addonId)));
        const apiPayload: Payload = {};
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('post', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'addon',
            models: {
                addon: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        $permissions: [],
                        key: '',
                        resourceType: '',
                        resourceId: '',
                        status: 'active',
                        currentValue: 0,
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Get the price details for a billing addon for a project, including the prorated amount for the remaining days in the current billing cycle.
     *
     *
     * @param {string} params.projectId - Project ID
     * @param {string} params.addon - Addon key identifier (e.g. premiumGeoDB).
     * @throws {AppwriteException}
     * @returns {Promise<Models.AddonPrice>}
     */
    getAddonPrice(params: {
        projectId: string;
        addon: string;
    }): Promise<Models.AddonPrice>;
    /**
     * Get the price details for a billing addon for a project, including the prorated amount for the remaining days in the current billing cycle.
     *
     *
     * @param {string} projectId - Project ID
     * @param {string} addon - Addon key identifier (e.g. premiumGeoDB).
     * @throws {AppwriteException}
     * @returns {Promise<Models.AddonPrice>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    getAddonPrice(projectId: string, addon: string): Promise<Models.AddonPrice>;
    getAddonPrice(
        paramsOrFirst: { projectId: string; addon: string } | string,
        ...rest: [string?]
    ): Promise<Models.AddonPrice> {
        let params: { projectId: string; addon: string };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                projectId: string;
                addon: string;
            };
        } else {
            params = {
                projectId: paramsOrFirst as string,
                addon: rest[0] as string,
            };
        }

        const projectId = params.projectId;
        const addon = params.addon;

        if (typeof projectId === 'undefined' || projectId === '') {
            throw new AppwriteException(
                'Missing required parameter: "projectId"',
            );
        }
        if (typeof addon === 'undefined' || addon === '') {
            throw new AppwriteException('Missing required parameter: "addon"');
        }
        const apiPath = '/projects/{projectId}/addons/{addon}/price'
            .replace('{projectId}', encodeURIComponent(String(projectId)))
            .replace('{addon}', encodeURIComponent(String(addon)));
        const apiPayload: Payload = {};
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            accept: 'application/json',
        };

        const request = this.client.call('get', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'addonPrice',
            models: {
                addonPrice: {
                    defaults: {
                        addonKey: '',
                        name: '',
                        monthlyPrice: 0,
                        proratedAmount: 0,
                        remainingDays: 0,
                        totalCycleDays: 0,
                        currency: 'USD',
                        billingCycleEnd: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Record console access to a project. This endpoint updates the last accessed timestamp for the project to track console activity.
     *
     *
     * @param {string} params.projectId - Project ID
     * @throws {AppwriteException}
     * @returns {Promise<{}>}
     */
    updateConsoleAccess(params: { projectId: string }): Promise<{}>;
    /**
     * Record console access to a project. This endpoint updates the last accessed timestamp for the project to track console activity.
     *
     *
     * @param {string} projectId - Project ID
     * @throws {AppwriteException}
     * @returns {Promise<{}>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updateConsoleAccess(projectId: string): Promise<{}>;
    updateConsoleAccess(
        paramsOrFirst: { projectId: string } | string,
    ): Promise<{}> {
        let params: { projectId: string };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as { projectId: string };
        } else {
            params = {
                projectId: paramsOrFirst as string,
            };
        }

        const projectId = params.projectId;

        if (typeof projectId === 'undefined' || projectId === '') {
            throw new AppwriteException(
                'Missing required parameter: "projectId"',
            );
        }
        const apiPath = '/projects/{projectId}/console-access'.replace(
            '{projectId}',
            encodeURIComponent(String(projectId)),
        );
        const apiPayload: Payload = {};
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        return this.client.call('patch', uri, apiHeaders, apiPayload);
    }

    /**
     * Get a list of all the project's schedules. You can use the query params to filter your results.
     *
     * @param {string} params.projectId - Project unique ID.
     * @param {string[]} params.queries - Array of query strings generated using the Query class provided by the SDK. [Learn more about queries](https://appwrite.io/docs/queries). Maximum of 100 queries are allowed, each 4096 characters long. You may filter on the following attributes: resourceType, resourceId, projectId, schedule, active, region
     * @param {boolean} params.total - When set to false, the total count returned will be 0 and will not be calculated.
     * @throws {AppwriteException}
     * @returns {Promise<Models.ScheduleList>}
     */
    listSchedules(params: {
        projectId: string;
        queries?: string[];
        total?: boolean;
    }): Promise<Models.ScheduleList>;
    /**
     * Get a list of all the project's schedules. You can use the query params to filter your results.
     *
     * @param {string} projectId - Project unique ID.
     * @param {string[]} queries - Array of query strings generated using the Query class provided by the SDK. [Learn more about queries](https://appwrite.io/docs/queries). Maximum of 100 queries are allowed, each 4096 characters long. You may filter on the following attributes: resourceType, resourceId, projectId, schedule, active, region
     * @param {boolean} total - When set to false, the total count returned will be 0 and will not be calculated.
     * @throws {AppwriteException}
     * @returns {Promise<Models.ScheduleList>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    listSchedules(
        projectId: string,
        queries?: string[],
        total?: boolean,
    ): Promise<Models.ScheduleList>;
    listSchedules(
        paramsOrFirst:
            { projectId: string; queries?: string[]; total?: boolean } | string,
        ...rest: [string[]?, boolean?]
    ): Promise<Models.ScheduleList> {
        let params: { projectId: string; queries?: string[]; total?: boolean };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                projectId: string;
                queries?: string[];
                total?: boolean;
            };
        } else {
            params = {
                projectId: paramsOrFirst as string,
                queries: rest[0] as string[],
                total: rest[1] as boolean,
            };
        }

        const projectId = params.projectId;
        const queries = params.queries;
        const total = params.total;

        if (typeof projectId === 'undefined' || projectId === '') {
            throw new AppwriteException(
                'Missing required parameter: "projectId"',
            );
        }
        const apiPath = '/projects/{projectId}/schedules'.replace(
            '{projectId}',
            encodeURIComponent(String(projectId)),
        );
        const apiPayload: Payload = {};
        if (typeof queries !== 'undefined') {
            apiPayload['queries'] = queries;
        }
        if (typeof total !== 'undefined') {
            apiPayload['total'] = total;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            accept: 'application/json',
        };

        const request = this.client.call('get', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'scheduleList',
            models: {
                scheduleList: {
                    defaults: {
                        total: 0,
                        schedules: [],
                    },
                    properties: {
                        schedules: {
                            items: 'schedule',
                        },
                    },
                },
                schedule: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        resourceType: '',
                        resourceId: '',
                        resourceUpdatedAt: '',
                        projectId: '',
                        schedule: '',
                        data: {},
                        active: false,
                        region: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Create a new schedule for a resource.
     *
     * @param {string} params.projectId - Project unique ID.
     * @param {ScheduleResourceType} params.resourceType - The resource type for the schedule. Possible values: function, execution, message, backup.
     * @param {string} params.resourceId - The resource ID to associate with this schedule.
     * @param {string} params.schedule - Schedule CRON expression. Cannot be combined with interval.
     * @param {boolean} params.active - Whether the schedule is active.
     * @param {object} params.data - Schedule data as a JSON string. Used to store resource-specific context needed for execution.
     * @param {number} params.interval - Minutes between runs, for function schedules only. Use 0 to disable. Cannot be combined with schedule.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Schedule>}
     */
    createSchedule(params: {
        projectId: string;
        resourceType: ScheduleResourceType;
        resourceId: string;
        schedule?: string;
        active?: boolean;
        data?: object;
        interval?: number;
    }): Promise<Models.Schedule>;
    /**
     * Create a new schedule for a resource.
     *
     * @param {string} projectId - Project unique ID.
     * @param {ScheduleResourceType} resourceType - The resource type for the schedule. Possible values: function, execution, message, backup.
     * @param {string} resourceId - The resource ID to associate with this schedule.
     * @param {string} schedule - Schedule CRON expression. Cannot be combined with interval.
     * @param {boolean} active - Whether the schedule is active.
     * @param {object} data - Schedule data as a JSON string. Used to store resource-specific context needed for execution.
     * @param {number} interval - Minutes between runs, for function schedules only. Use 0 to disable. Cannot be combined with schedule.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Schedule>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    createSchedule(
        projectId: string,
        resourceType: ScheduleResourceType,
        resourceId: string,
        schedule?: string,
        active?: boolean,
        data?: object,
        interval?: number,
    ): Promise<Models.Schedule>;
    createSchedule(
        paramsOrFirst:
            | {
                  projectId: string;
                  resourceType: ScheduleResourceType;
                  resourceId: string;
                  schedule?: string;
                  active?: boolean;
                  data?: object;
                  interval?: number;
              }
            | string,
        ...rest: [
            ScheduleResourceType?,
            string?,
            string?,
            boolean?,
            object?,
            number?,
        ]
    ): Promise<Models.Schedule> {
        let params: {
            projectId: string;
            resourceType: ScheduleResourceType;
            resourceId: string;
            schedule?: string;
            active?: boolean;
            data?: object;
            interval?: number;
        };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                projectId: string;
                resourceType: ScheduleResourceType;
                resourceId: string;
                schedule?: string;
                active?: boolean;
                data?: object;
                interval?: number;
            };
        } else {
            params = {
                projectId: paramsOrFirst as string,
                resourceType: rest[0] as ScheduleResourceType,
                resourceId: rest[1] as string,
                schedule: rest[2] as string,
                active: rest[3] as boolean,
                data: rest[4] as object,
                interval: rest[5] as number,
            };
        }

        const projectId = params.projectId;
        const resourceType = params.resourceType;
        const resourceId = params.resourceId;
        const schedule = params.schedule;
        const active = params.active;
        const data = params.data;
        const interval = params.interval;

        if (typeof projectId === 'undefined' || projectId === '') {
            throw new AppwriteException(
                'Missing required parameter: "projectId"',
            );
        }
        if (typeof resourceType === 'undefined') {
            throw new AppwriteException(
                'Missing required parameter: "resourceType"',
            );
        }
        if (typeof resourceId === 'undefined') {
            throw new AppwriteException(
                'Missing required parameter: "resourceId"',
            );
        }
        const apiPath = '/projects/{projectId}/schedules'.replace(
            '{projectId}',
            encodeURIComponent(String(projectId)),
        );
        const apiPayload: Payload = {};
        if (typeof resourceType !== 'undefined') {
            apiPayload['resourceType'] = resourceType;
        }
        if (typeof resourceId !== 'undefined') {
            apiPayload['resourceId'] = resourceId;
        }
        if (typeof schedule !== 'undefined') {
            apiPayload['schedule'] = schedule;
        }
        if (typeof active !== 'undefined') {
            apiPayload['active'] = active;
        }
        if (typeof data !== 'undefined') {
            apiPayload['data'] = data;
        }
        if (typeof interval !== 'undefined') {
            apiPayload['interval'] = interval;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('post', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'schedule',
            models: {
                schedule: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        resourceType: '',
                        resourceId: '',
                        resourceUpdatedAt: '',
                        projectId: '',
                        schedule: '',
                        data: {},
                        active: false,
                        region: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Get a schedule by its unique ID.
     *
     * @param {string} params.projectId - Project unique ID.
     * @param {string} params.scheduleId - Schedule ID.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Schedule>}
     */
    getSchedule(params: {
        projectId: string;
        scheduleId: string;
    }): Promise<Models.Schedule>;
    /**
     * Get a schedule by its unique ID.
     *
     * @param {string} projectId - Project unique ID.
     * @param {string} scheduleId - Schedule ID.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Schedule>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    getSchedule(
        projectId: string,
        scheduleId: string,
    ): Promise<Models.Schedule>;
    getSchedule(
        paramsOrFirst: { projectId: string; scheduleId: string } | string,
        ...rest: [string?]
    ): Promise<Models.Schedule> {
        let params: { projectId: string; scheduleId: string };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                projectId: string;
                scheduleId: string;
            };
        } else {
            params = {
                projectId: paramsOrFirst as string,
                scheduleId: rest[0] as string,
            };
        }

        const projectId = params.projectId;
        const scheduleId = params.scheduleId;

        if (typeof projectId === 'undefined' || projectId === '') {
            throw new AppwriteException(
                'Missing required parameter: "projectId"',
            );
        }
        if (typeof scheduleId === 'undefined' || scheduleId === '') {
            throw new AppwriteException(
                'Missing required parameter: "scheduleId"',
            );
        }
        const apiPath = '/projects/{projectId}/schedules/{scheduleId}'
            .replace('{projectId}', encodeURIComponent(String(projectId)))
            .replace('{scheduleId}', encodeURIComponent(String(scheduleId)));
        const apiPayload: Payload = {};
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            accept: 'application/json',
        };

        const request = this.client.call('get', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'schedule',
            models: {
                schedule: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        resourceType: '',
                        resourceId: '',
                        resourceUpdatedAt: '',
                        projectId: '',
                        schedule: '',
                        data: {},
                        active: false,
                        region: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Get the onboarding stages for the current project, including each stage’s SDK method key and status (for example pending, completed, or skipped).
     *
     *
     * @param {string} params.projectId - Project unique ID.
     * @throws {AppwriteException}
     * @returns {Promise<Models.StageList>}
     */
    listStages(params: { projectId: string }): Promise<Models.StageList>;
    /**
     * Get the onboarding stages for the current project, including each stage’s SDK method key and status (for example pending, completed, or skipped).
     *
     *
     * @param {string} projectId - Project unique ID.
     * @throws {AppwriteException}
     * @returns {Promise<Models.StageList>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    listStages(projectId: string): Promise<Models.StageList>;
    listStages(
        paramsOrFirst: { projectId: string } | string,
    ): Promise<Models.StageList> {
        let params: { projectId: string };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as { projectId: string };
        } else {
            params = {
                projectId: paramsOrFirst as string,
            };
        }

        const projectId = params.projectId;

        if (typeof projectId === 'undefined' || projectId === '') {
            throw new AppwriteException(
                'Missing required parameter: "projectId"',
            );
        }
        const apiPath = '/projects/{projectId}/stages'.replace(
            '{projectId}',
            encodeURIComponent(String(projectId)),
        );
        const apiPayload: Payload = {};
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            accept: 'application/json',
        };

        const request = this.client.call('get', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'stageList',
            models: {
                stageList: {
                    defaults: {
                        stages: [],
                    },
                    properties: {
                        stages: {
                            items: 'stage',
                        },
                    },
                },
                stage: {
                    defaults: {
                        id: '',
                        sdk: '',
                        status: 'pending',
                        at: '',
                        actorType: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Update an onboarding stage for the current project. Use this endpoint to skip a stage or leave it unchanged without performing the related API action.
     *
     *
     * @param {string} params.projectId - Project unique ID.
     * @param {string} params.stageId - SDK method key (namespace.method).
     * @param {boolean} params.skip - Mark the stage as skipped.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Stage>}
     */
    updateStage(params: {
        projectId: string;
        stageId: string;
        skip?: boolean;
    }): Promise<Models.Stage>;
    /**
     * Update an onboarding stage for the current project. Use this endpoint to skip a stage or leave it unchanged without performing the related API action.
     *
     *
     * @param {string} projectId - Project unique ID.
     * @param {string} stageId - SDK method key (namespace.method).
     * @param {boolean} skip - Mark the stage as skipped.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Stage>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updateStage(
        projectId: string,
        stageId: string,
        skip?: boolean,
    ): Promise<Models.Stage>;
    updateStage(
        paramsOrFirst:
            { projectId: string; stageId: string; skip?: boolean } | string,
        ...rest: [string?, boolean?]
    ): Promise<Models.Stage> {
        let params: { projectId: string; stageId: string; skip?: boolean };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                projectId: string;
                stageId: string;
                skip?: boolean;
            };
        } else {
            params = {
                projectId: paramsOrFirst as string,
                stageId: rest[0] as string,
                skip: rest[1] as boolean,
            };
        }

        const projectId = params.projectId;
        const stageId = params.stageId;
        const skip = params.skip;

        if (typeof projectId === 'undefined' || projectId === '') {
            throw new AppwriteException(
                'Missing required parameter: "projectId"',
            );
        }
        if (typeof stageId === 'undefined' || stageId === '') {
            throw new AppwriteException(
                'Missing required parameter: "stageId"',
            );
        }
        const apiPath = '/projects/{projectId}/stages/{stageId}'
            .replace('{projectId}', encodeURIComponent(String(projectId)))
            .replace('{stageId}', encodeURIComponent(String(stageId)));
        const apiPayload: Payload = {};
        if (typeof skip !== 'undefined') {
            apiPayload['skip'] = skip;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('patch', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'stage',
            models: {
                stage: {
                    defaults: {
                        id: '',
                        sdk: '',
                        status: 'pending',
                        at: '',
                        actorType: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Update the status of a project. Can be used to archive/restore projects, and to restore paused projects. When restoring a paused project, the console fingerprint header must be provided and the project must not be blocked for any reason other than inactivity.
     *
     *
     * @param {string} params.projectId - Project ID
     * @param {Status} params.status - New status for the project
     * @throws {AppwriteException}
     * @returns {Promise<{}>}
     */
    updateStatus(params: { projectId: string; status: Status }): Promise<{}>;
    /**
     * Update the status of a project. Can be used to archive/restore projects, and to restore paused projects. When restoring a paused project, the console fingerprint header must be provided and the project must not be blocked for any reason other than inactivity.
     *
     *
     * @param {string} projectId - Project ID
     * @param {Status} status - New status for the project
     * @throws {AppwriteException}
     * @returns {Promise<{}>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updateStatus(projectId: string, status: Status): Promise<{}>;
    updateStatus(
        paramsOrFirst: { projectId: string; status: Status } | string,
        ...rest: [Status?]
    ): Promise<{}> {
        let params: { projectId: string; status: Status };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                projectId: string;
                status: Status;
            };
        } else {
            params = {
                projectId: paramsOrFirst as string,
                status: rest[0] as Status,
            };
        }

        const projectId = params.projectId;
        const status = params.status;

        if (typeof projectId === 'undefined' || projectId === '') {
            throw new AppwriteException(
                'Missing required parameter: "projectId"',
            );
        }
        if (typeof status === 'undefined') {
            throw new AppwriteException('Missing required parameter: "status"');
        }
        const apiPath = '/projects/{projectId}/status'.replace(
            '{projectId}',
            encodeURIComponent(String(projectId)),
        );
        const apiPayload: Payload = {};
        if (typeof status !== 'undefined') {
            apiPayload['status'] = status;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        return this.client.call('patch', uri, apiHeaders, apiPayload);
    }

    /**
     * Update the team ID of a project allowing for it to be transferred to another team.
     *
     * @param {string} params.projectId - Project unique ID.
     * @param {string} params.teamId - Team ID of the team to transfer project to.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Project>}
     */
    updateTeam(params: {
        projectId: string;
        teamId: string;
    }): Promise<Models.Project>;
    /**
     * Update the team ID of a project allowing for it to be transferred to another team.
     *
     * @param {string} projectId - Project unique ID.
     * @param {string} teamId - Team ID of the team to transfer project to.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Project>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updateTeam(projectId: string, teamId: string): Promise<Models.Project>;
    updateTeam(
        paramsOrFirst: { projectId: string; teamId: string } | string,
        ...rest: [string?]
    ): Promise<Models.Project> {
        let params: { projectId: string; teamId: string };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                projectId: string;
                teamId: string;
            };
        } else {
            params = {
                projectId: paramsOrFirst as string,
                teamId: rest[0] as string,
            };
        }

        const projectId = params.projectId;
        const teamId = params.teamId;

        if (typeof projectId === 'undefined' || projectId === '') {
            throw new AppwriteException(
                'Missing required parameter: "projectId"',
            );
        }
        if (typeof teamId === 'undefined') {
            throw new AppwriteException('Missing required parameter: "teamId"');
        }
        const apiPath = '/projects/{projectId}/team'.replace(
            '{projectId}',
            encodeURIComponent(String(projectId)),
        );
        const apiPayload: Payload = {};
        if (typeof teamId !== 'undefined') {
            apiPayload['teamId'] = teamId;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('patch', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'project',
            models: {
                project: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        name: '',
                        teamId: '',
                        region: 'fra',
                        smtpEnabled: false,
                        smtpSenderName: '',
                        smtpSenderEmail: '',
                        smtpReplyToName: '',
                        smtpReplyToEmail: '',
                        smtpHost: '',
                        smtpPort: 0,
                        smtpUsername: '',
                        smtpPassword: '',
                        smtpSecure: '',
                        pingCount: 0,
                        pingedAt: '',
                        labels: [],
                        status: 'active',
                        onboarding: {},
                        authMethods: [],
                        services: [],
                        protocols: [],
                        blocks: [],
                        consoleAccessedAt: '',
                    },
                    properties: {
                        authMethods: {
                            items: 'projectAuthMethod',
                        },
                        services: {
                            items: 'projectService',
                        },
                        protocols: {
                            items: 'projectProtocol',
                        },
                        blocks: {
                            items: 'block',
                        },
                    },
                },
                projectAuthMethod: {
                    defaults: {
                        enabled: true,
                    },
                },
                projectService: {
                    defaults: {
                        enabled: true,
                    },
                },
                projectProtocol: {
                    defaults: {
                        enabled: true,
                    },
                },
                block: {
                    defaults: {
                        $createdAt: '',
                        resourceType: '',
                        resourceId: '',
                        mode: 'full',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }
}
